import * as PIXI from 'pixi.js';
import store from '../../../GameKitUI/store';
import LibraryManager from '../../../Game/Base/Library/LibraryManager';
import GSOParasite from '../../../Game/Base/GSOParasite';
import ClickVoid from '../ClickVoid';
import Global from '../../../Game/Global';
import Vector from '../../../Game/Base/Math/Vector';
import { setActiveSidebarItem } from '../../../GameKitUI/actions/sideBarActions';
import { SideBarItems } from '../../../GameKitUI/shared';
import { backgroundEditorSetTile } from '../../../GameKitUI/actions/levelEditorActions';
import AssetManager from '../../../Game/Base/AssetManager';

export default class TileEditor extends GSOParasite {
    constructor(levelEditor, stepParent) {
        super(levelEditor, stepParent);
        this.levelEditor = levelEditor;
        this.gameKit = this.levelEditor.gameKit;

        this.pixiContainer = null;
        this.gridSprite = null;

        // The click void is a screen filling button, perfect for catching clicks on the canvas without catching events for overlayed buttons.
        this.clickVoidButton = new ClickVoid(this);
        this.clickVoidButton.zIndex = -9999;
        this.clickVoidButton.lastPosition = null;
        this.clickVoidButton.onMouseDown = mousePositionP => {
            this.setTileTexture(mousePositionP);
            this.clickVoidButton.lastPosition = mousePositionP.copy();
        };
        this.clickVoidButton.onMouseMove = mousePositionP => {
            const delta = mousePositionP.sub(this.clickVoidButton.lastPosition);
            for (let i = 0; i < delta.length(); i++) {
                const checkPos = delta
                    .normalized()
                    .mul(new Vector(i))
                    .add(this.clickVoidButton.lastPosition);
                this.setTileTexture(checkPos);
            }
            this.setTileTexture(mousePositionP);
            this.clickVoidButton.lastPosition = mousePositionP.copy();
        };
        this.clickVoidButton.onMouseUp = mousePositionP => {};

        this._brushTextureID = 'wall';
        this._lastBrushTextureID = 'wall';
    }

    onDetach() {
        const { level } = this.levelEditor;
        for (let i = 0; i < level.blokken.length; i++) {
            const blok = level.blokken[i];
            blok.requestNewVisuals();
        }
    }

    onCreateVisuals() {
        // The PIXI container acts as the root for this editor's graphics.
        this.pixiContainer = new PIXI.Container();

        this.getAttachedStepParent().pixiContainerBackgroundOverlayLayer.addChild(this.pixiContainer); // This attaches the overlay to the level graphics.
        // this.getAttachedStepParent() is the level that the overlay is attached to.

        this.gridSprite = new PIXI.extras.TilingSprite(
            AssetManager.get('img.grid'),
            this.getAttachedStepParent().size.x * Global.NATIVE_BLOCK_SIZE,
            this.getAttachedStepParent().size.y * Global.NATIVE_BLOCK_SIZE
        );
        this.pixiContainer.addChild(this.gridSprite);

        const level = this.getAttachedStepParent();
        for (let i = 0; i < level.blokken.length; i++) {
            const blok = level.blokken[i];
            blok.createSimplifiedVisuals();
        }
        const filter = new PIXI.filters.AlphaFilter(0.5);
        level.pixiContainerBlokLayer.filters = [filter];
    }

    onUpdateVisuals() {
        const level = this.getAttachedStepParent();
        this.gridSprite.width = level.size.x * Global.NATIVE_BLOCK_SIZE;
        this.gridSprite.height = level.size.y * Global.NATIVE_BLOCK_SIZE;
    }

    onDestroyVisuals() {
        // Destroy all PIXI entities that were introduced by this class.
        this.pixiContainer.destroy();
        if (this.gridSprite) this.gridSprite.destroy();
        this.gridSprite = null;

        const level = this.getAttachedStepParent();
        if (level.pixiContainerBlokLayer) level.pixiContainerBlokLayer.filters = [];
    }

    setTileTexture(mousePositionP) {
        const mousePosition = this.getGame().canvasToGamePosition(mousePositionP);

        const pos = new Vector(Math.floor(mousePosition.x / Global.NATIVE_BLOCK_SIZE), Math.floor(mousePosition.y / Global.NATIVE_BLOCK_SIZE));
        pos.x -= this.levelEditor.level.position.x;
        pos.y -= this.levelEditor.level.position.y;

        if (pos.x < 0) return;
        if (pos.y < 0) return;
        if (pos.x >= this.levelEditor.level.size.x) return;
        if (pos.y >= this.levelEditor.level.size.y) return;

        const current = this.levelEditor.level.background.columns[pos.x][pos.y];

        if (current !== this._brushTextureID) {
            this.levelEditor.level.background.columns[pos.x][pos.y] = this._brushTextureID;
            this.levelEditor.level.background.requestNewVisuals();
        }
    }

    setBrushTexture(textureID, dispatch = true) {
        this._brushTextureID = textureID;
        this._lastBrushTextureID = textureID;

        if (dispatch) {
            // Update de store
            store.dispatch(backgroundEditorSetTile(textureID));
        }
    }

    startDrawMode(dispatch = true) {
        if (this._brushTextureID === 0) {
            this._brushTextureID = this._lastBrushTextureID;
        }
        if (dispatch) {
            // Update de store
            store.dispatch(setActiveSidebarItem(SideBarItems.BACKGROUND_DRAW));
        }
    }

    startEraseMode(dispatch = true) {
        if (this._brushTextureID !== 0) {
            this._lastBrushTextureID = this._brushTextureID;
            this._brushTextureID = 0;
        }
        if (dispatch) {
            // Update de store
            store.dispatch(setActiveSidebarItem(SideBarItems.BACKGROUND_ERASE));
        }
    }
}
